import log from '@/lib/logger';
import { useState, useEffect } from 'react';
import { Button } from '@/components/tailwind/button';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components/tailwind/dialog';
import { Field, Label } from '@/components/tailwind/fieldset';
import { Textarea } from '@/components/tailwind/textarea';
import { useCommonTranslation, useOrdersTranslation } from '@/i18n/hooks';
import { getOrderEmailPrecheck, sendOrderEmail } from '@/services/order-services';
import type { OrderEmailPrecheck } from '@/services/order-services';
import { useAuth } from '@/context/use-auth';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';

interface OrderSendEmailDialogProps {
    orderId: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export function OrderSendEmailDialog({ orderId, isOpen, onClose, onSuccess }: OrderSendEmailDialogProps) {
    const { t } = useOrdersTranslation();
    const { t: tCommon } = useCommonTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const [additionalMessage, setAdditionalMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [precheck, setPrecheck] = useState<OrderEmailPrecheck | null>(null);

    useEffect(() => {
        if (isOpen) {
            loadPrecheck();
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    async function loadPrecheck() {
        setIsLoading(true);
        setPrecheck(null);
        try {
            const result = await getOrderEmailPrecheck(token, orderId);
            if (result) {
                setPrecheck(result);
            }
        } catch (error) {
            log.error('Failed to load email precheck:', error);
        } finally {
            setIsLoading(false);
        }
    }

    function handleClose() {
        setAdditionalMessage('');
        setPrecheck(null);
        onClose();
    }

    async function handleSubmit() {
        setIsSubmitting(true);
        try {
            const result = await sendOrderEmail(token, orderId, additionalMessage.trim() || undefined);
            if (result) {
                toast({
                    title: t('sendEmailDialog.toast.success.title'),
                    description: t('sendEmailDialog.toast.success.description', {
                        helperName: result.helperName,
                    }),
                    variant: 'default',
                });
            }
            handleClose();
            onSuccess();
        } catch (error) {
            log.error('Failed to send order email:', error);
            toast({
                title: t('sendEmailDialog.toast.error.title'),
                description: t('sendEmailDialog.toast.error.description'),
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    function canSend(): boolean {
        if (!precheck) return false;
        return precheck.helperAssigned
            && precheck.helperHasEmail
            && precheck.emailCapabilityAvailable
            && precheck.templateConfigured;
    }

    function renderErrors() {
        if (!precheck) return null;
        const errors: string[] = [];
        if (!precheck.helperAssigned) errors.push(t('sendEmailDialog.errors.noHelper'));
        if (precheck.helperAssigned && !precheck.helperHasEmail) errors.push(t('sendEmailDialog.errors.noHelperEmail'));
        if (!precheck.emailCapabilityAvailable) errors.push(t('sendEmailDialog.errors.noEmailCapability'));
        if (!precheck.templateConfigured) errors.push(t('sendEmailDialog.errors.noTemplate'));

        if (errors.length === 0) return null;

        return (
            <div className="rounded-md bg-red-50 dark:bg-red-950 border-l-4 border-red-400 dark:border-red-500 p-4">
                <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-500 flex-shrink-0" />
                    <div className="ml-3">
                        {errors.map((error, index) => (
                            <p key={index} className="text-sm text-red-700 dark:text-red-300">
                                {error}
                            </p>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Dialog open={isOpen} onClose={handleClose}>
            <DialogTitle>{t('sendEmailDialog.title')}</DialogTitle>
            <DialogDescription>{t('sendEmailDialog.description')}</DialogDescription>
            <DialogBody>
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                    </div>
                )}

                {!isLoading && precheck && (
                    <div className="space-y-4">
                        {renderErrors()}

                        {precheck.submissionChannelIsEmail && precheck.helperAssigned && !precheck.helperHasEmail && (
                            <div className="rounded-md bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500 p-4">
                                <div className="flex">
                                    <AlertTriangle className="h-5 w-5 text-amber-400 dark:text-amber-500 flex-shrink-0" />
                                    <p className="ml-3 text-sm text-amber-700 dark:text-amber-300">
                                        {t('sendEmailDialog.warnings.emailChannelNoEmail')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {canSend() && (
                            <>
                                <div className="rounded-md bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4">
                                    <div className="flex">
                                        <Info className="h-5 w-5 text-blue-400 dark:text-blue-500 flex-shrink-0" />
                                        <p className="ml-3 text-sm text-blue-700 dark:text-blue-300">
                                            {t('sendEmailDialog.recipient', {
                                                helperName: precheck.helperName,
                                                helperEmail: precheck.helperEmail,
                                            })}
                                        </p>
                                    </div>
                                </div>

                                {precheck.submissionChannelIsEmail && (
                                    <div className="rounded-md bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4">
                                        <div className="flex">
                                            <Info className="h-5 w-5 text-blue-400 dark:text-blue-500 flex-shrink-0" />
                                            <p className="ml-3 text-sm text-blue-700 dark:text-blue-300">
                                                {t('sendEmailDialog.submissionChannelEmailInfo')}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <Field>
                                    <Label>{t('sendEmailDialog.additionalMessageLabel')}</Label>
                                    <Textarea
                                        name="additionalMessage"
                                        rows={4}
                                        value={additionalMessage}
                                        onChange={(e) => setAdditionalMessage(e.target.value)}
                                        placeholder={t('sendEmailDialog.additionalMessagePlaceholder')}
                                        data-testid="order-email-additional-message"
                                    />
                                </Field>
                            </>
                        )}
                    </div>
                )}
            </DialogBody>
            <DialogActions>
                <Button plain onClick={handleClose} disabled={isSubmitting}>
                    {tCommon('buttons.cancel.text')}
                </Button>
                <Button
                    color="blue"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isLoading || !canSend()}
                    data-testid="order-email-send-button"
                >
                    {isSubmitting ? t('sendEmailDialog.sending') : t('sendEmailDialog.send')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
